<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.caen_code, 'Cod CAEN')"
      :items="caenCodes"
      :item-title="customItemTitle"
      item-value="code"
      v-model="selectedCaenCode"
      :rules="[v => !!v || translation(translationList.required, 'Acest câmp este obligatoriu')]"
  >
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, toRefs, watch } from 'vue';
import { useCompanyStore } from '~/plugins/store/company';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type { ApiCompanyCaen } from '~/types/Customer';
import type { TranslationList } from '~/types/Utils';

export default defineComponent({
  props: {
    modelValue: {
      type: String as () => string | null | number,
      default: '',
      required: false
    },
    customerIndex: Number as () => number | null,
  },
  emits: ['update:modelValue', 'validation-customer-status'],
  setup(props, { emit }) {
    const { modelValue, customerIndex } = toRefs(props);
    const companyStore = useCompanyStore();
    const translationStore = useTranslationStore();
    const selectedCaenCode = ref(modelValue.value);
    const caenCodes = ref<ApiCompanyCaen[]>([]);
    const translationList = ref<TranslationList>({});
    let initialSetupDone = false;
    const error = ref([]);

    onMounted(async () => {
      caenCodes.value = await companyStore.getCompanyCaenList();
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      initialSetupDone = true;
      const isValid = !!selectedCaenCode.value;
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `caen_code-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: 'caen_code', isValid });
      }

      // Listen for validate event
      window.addEventListener('validate-customer', (data) => {
        if (data.detail && (data.detail === `caen_code-${customerIndex.value}` || data.detail === 'caen_code')) {
          if (!selectedCaenCode.value) {
            error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
          } else {
            error.value = [];
            emit('validation-customer-status', { field: 'caen_code', isValid: true });
          }
        }
      });
    });

    watch(selectedCaenCode, (newCaenCode) => {
      if (initialSetupDone) {
        emit('update:modelValue', newCaenCode);
      }
    });

    watch(modelValue, (newValue) => {
      const found = caenCodes.value.find((item) => item.code === newValue);
      error.value = [];
      if (found) {
        selectedCaenCode.value = newValue;
      } else if (newValue != null && newValue !== '') {
        selectedCaenCode.value = null;
        console.error('Caen code not found in the list');
      }

      const isValid = !!newValue;
      if (customerIndex.value !== undefined) {
        emit('validation-customer-status', { field: `caen_code-${customerIndex.value}`, isValid });
      } else {
        emit('validation-customer-status', { field: 'caen_code', isValid });
      }
    });

    const customItemTitle = (item: ApiCompanyCaen) => `${item.code} - ${item.name}`;

    return {
      selectedCaenCode,
      translationList,
      translation,
      caenCodes,
      error,
      customItemTitle
    };
  },
});
</script>

<style scoped>
</style>